import { TypographyCaption } from '@heidi/design-system/typography';
import { FormattedMessage } from 'react-intl';

export interface SourceSearchCapBarProps {
  /** Total per-collection ceiling — shown verbatim in the destructive
   *  caption so the user sees the absolute number, not just the
   *  remaining slots. */
  cap: number;
  /** Live count of slots free after deducting already-added and
   *  currently-pending domains. Drives the informational counter. */
  remaining: number;
  /** When true, the destructive caption replaces the counter. Driven
   *  by `useCapAwareMultiSelect`'s `showCapError` — either the user
   *  attempted to over-select or the collection itself is already at
   *  capacity. */
  showError: boolean;
}

/**
 * Sticky one-line header rendered above the source-search dropdown's
 * scrollable result viewport. Surfaces the per-collection cap as either
 *
 *  - an informational "{N} more can be added" counter, OR
 *  - a destructive "you can only add up to {cap}" caption when the user
 *    has attempted to over-select.
 *
 * Lives in its own file so `SourceSearchInput.tsx` stays under the
 * codebase-wide `max-lines: 300` cap, and so the bar can be unit-tested
 * in isolation of the dropdown plumbing.
 */
export const SourceSearchCapBar = ({
  cap,
  remaining,
  showError,
}: SourceSearchCapBarProps) => (
  <div
    className="flex shrink-0 items-center justify-end border-b border-border bg-surface px-3 py-1.5"
    data-testid="source-search-cap-bar"
  >
    {showError ? (
      <TypographyCaption
        className="text-validation-destructive"
        data-testid="source-search-cap-error"
      >
        <FormattedMessage
          defaultMessage="You can only add up to {max, plural, one {# source} other {# sources}}. Remove some to add more."
          id="qq6mAILGH6"
          description="Destructive caption shown in the source-search dropdown when the user tries to select more sources than the per-collection cap allows."
          values={{ max: cap }}
        />
      </TypographyCaption>
    ) : (
      <TypographyCaption
        className="text-text-tertiary"
        data-testid="source-search-cap-remaining"
      >
        <FormattedMessage
          defaultMessage="{count, plural, one {# more source can be added} other {# more sources can be added}}"
          id="lqf8DRVV+B"
          description="Counter shown in the source-search dropdown telling the user how many more sources they can still add before hitting the per-collection cap."
          values={{ count: remaining }}
        />
      </TypographyCaption>
    )}
  </div>
);
